import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import logo from "../assets/img/logo.svg";
import { SITE } from "../config/site";
import { CallButton, EmailLink } from "./ContactLinks";
import { useEscape, useLockBodyScroll } from "../lib/hooks";
import { useI18n } from "../i18n";
import LanguageSwitcher from "../i18n/LanguageSwitcher";

// Paths are language-neutral; useNavLinks() localizes them.
const NAV_LINKS = [
  { to: "/#ponuda", key: "offer" },
  { to: "/katalog", key: "units", route: true },
  { to: "/#kako-gradimo", key: "process" },
  { to: "/#o-nama", key: "about" },
  { to: "/#lokacija", key: "location" },
  { to: "/#kontakt", key: "contact" },
];

export function useNavLinks() {
  const { t, lp } = useI18n();
  return NAV_LINKS.map((link) => ({ ...link, to: lp(link.to), label: t(`nav.${link.key}`) }));
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t, lp } = useI18n();
  const links = useNavLinks();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname, location.hash]);
  useEscape(() => setOpen(false), open);
  useLockBodyScroll(open);

  return (
    <header className={`site-nav ${scrolled || open ? "is-solid" : ""}`}>
      <div className="container site-nav-inner">
        <Link to={lp("/")} className="site-nav-logo" aria-label={t("nav.logoHome")}>
          <img src={logo} alt="Tehnočin DOO Invest" width="120" height="78" />
        </Link>

        <nav className="site-nav-links" aria-label={t("nav.main")}>
          {links.map((link) =>
            link.route ? (
              <NavLink key={link.key} to={link.to} className="site-nav-link">
                {link.label}
              </NavLink>
            ) : (
              <Link key={link.key} to={link.to} className="site-nav-link">
                {link.label}
              </Link>
            )
          )}
        </nav>

        <LanguageSwitcher />

        <CallButton className="btn btn--sm site-nav-call" source="navigacija">
          {SITE.phoneDisplay}
        </CallButton>

        <button
          type="button"
          className="site-nav-toggle"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? t("nav.close") : t("nav.open")}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <div id="mobile-menu" className={`mobile-menu ${open ? "is-open" : ""}`} hidden={!open}>
        <nav aria-label={t("nav.mobile")}>
          {links.map((link) => (
            <Link key={link.key} to={link.to} className="mobile-menu-link">
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="mobile-menu-foot">
          <LanguageSwitcher />
          <CallButton className="btn btn--accent btn--lg btn--block" source="meni">
            {t("common.call")} {SITE.phoneDisplay}
          </CallButton>
          <EmailLink className="mobile-menu-mail" source="meni" />
        </div>
      </div>
    </header>
  );
}

export function MobileActionBar({ inquiryHref = "#kontakt", inquiryLabel, onInquiry }) {
  const { t } = useI18n();
  const label = inquiryLabel || t("common.sendInquiryShort");
  return (
    <div className="mobile-bar" data-testid="mobile-bar">
      <CallButton className="btn btn--accent" source="mobilna-traka">
        {t("common.callShort")}
      </CallButton>
      {onInquiry ? (
        <button type="button" className="btn btn--light" onClick={onInquiry}>
          {label}
        </button>
      ) : (
        <a href={inquiryHref} className="btn btn--light">
          {label}
        </a>
      )}
    </div>
  );
}
